import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { ContentStateProperty, InputType } from '../../../../@types/types'
import { InputProps } from '../../../../@types/props-types'
import {
  formatNumberForCalcWithDefault,
  formatNumberForDisplayWithOneDecimalPosition,
} from '../../../../i18next/decimal'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { containsNonNumericCharacter } from '../inputCharacterValidation'

export interface StepperProps extends InputProps {
  className?: string
  type?: InputType
  style?: React.CSSProperties
  placeholder?: string
  label?: string
  labelNode?: React.ReactNode
  input?: string
  min?: number
  max?: number
  maxLength?: number
  step?: number
  errors: string[]
  doOnFieldChange: (field: ContentStateProperty, value: string | undefined) => void
  onBlur?: () => void
  onBlurTracking?: (field: ContentStateProperty, value: string | undefined, isUnchanged: boolean) => void
  onFocus?: () => void
  hideMandatorySign?: boolean
  dataHeyimmoContext?: string
  numericOnly?: boolean
}

interface StepperState {
  isUnchanged: boolean
  lastValue: string | undefined
  currentInput: string | undefined
}

export const Stepper: React.FunctionComponent<StepperProps> = ({
  id,
  className,
  type,
  propertyName,
  style,
  label,
  labelNode,
  input,
  maxLength,
  errors,
  placeholder = '',
  min = 1,
  max = 100,
  step = 1,
  doOnFieldChange,
  onBlur,
  onBlurTracking,
  onFocus,
  hideMandatorySign = false,
  dataHeyimmoContext,
  numericOnly = false,
}: StepperProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const [state, setState] = useState<StepperState>({
    isUnchanged: true,
    lastValue: input,
    currentInput: input,
  })

  React.useEffect(
    () =>
      setState({
        isUnchanged: true,
        lastValue: input,
        currentInput: input,
      }),
    [input]
  )

  const onLostFocus = ({
    relatedTarget,
    currentTarget,
  }: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLButtonElement>) => {
    if (relatedTarget && [id, 'stepper-decrement-button', 'stepper-increment-button'].includes(relatedTarget.id)) {
      return
    }
    onBlur && onBlur()
    setTouched(propertyName)
    let realNewValue: string | undefined = currentTarget.value
    if (currentTarget && ['stepper-decrement-button', 'stepper-increment-button'].includes(currentTarget.id)) {
      realNewValue = state.currentInput
    }
    if (state.lastValue !== realNewValue) {
      onBlurTracking && onBlurTracking(propertyName, realNewValue, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: realNewValue, isUnchanged: false }))
    }
  }

  const hasErrors = () => errors.length > 0 && (formState.submitCount > 0 || formState.touched[propertyName])
  const newStyles = style ? style : ({} as React.CSSProperties)
  const getErrorClass = () => (hasErrors() ? ' error' : '')
  const getSuccessClass = () => (input !== undefined && !hasErrors() ? ' success-border' : '')

  const stepDown = () => {
    const number = formatNumberForCalcWithDefault(state.currentInput, null)
    if (number === null) {
      doOnFieldChange(propertyName, String(min))
      setState((prev) => ({ ...prev, currentInput: String(min) }))
    } else {
      const newValue = String(number <= max ? formatNumberForDisplayWithOneDecimalPosition(number - step) : max)
      min < number && doOnFieldChange(propertyName, newValue)
      min < number && setState((prev) => ({ ...prev, currentInput: newValue }))
    }
  }

  const stepUp = () => {
    const number = formatNumberForCalcWithDefault(state.currentInput, null)
    if (number === null) {
      doOnFieldChange(propertyName, String(min))
      setState((prev) => ({ ...prev, currentInput: String(min) }))
    } else {
      const newValue = String(number < min ? min : formatNumberForDisplayWithOneDecimalPosition(number + step))
      if (max > number) {
        doOnFieldChange(propertyName, newValue)
        setState((prev) => ({ ...prev, currentInput: newValue }))
      }
    }
  }

  const doChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (numericOnly && containsNonNumericCharacter(value)) {
      event.currentTarget.value = state.currentInput ?? ''
      return
    }

    doOnFieldChange(propertyName, value)
    setState((prev) => ({ ...prev, currentInput: value }))
  }

  return (
    <div className={`input-text-container ${className ? className : ''}`} role='group'>
      {label && <InputLabel id={id} labelTranslationKey={label} propertyName={propertyName} />}
      {labelNode && (
        <InputLabel id={id} labelNode={labelNode} propertyName={propertyName} hideMandatorySign={hideMandatorySign} />
      )}
      <div className='stepper-input-container'>
        <button
          tabIndex={0}
          type='button'
          aria-label='Minus'
          aria-describedby={id + '-input-label'}
          className='stepper-decrement-button'
          id='stepper-decrement-button'
          onClick={stepDown}
          onBlur={onLostFocus}
          {...(onFocus && { onFocus: onFocus })}
        >
          -
        </button>
        <input
          tabIndex={0}
          type={type}
          id={id}
          className={'input-text ' + getErrorClass() + getSuccessClass()}
          placeholder={placeholder}
          value={state.currentInput ?? ''}
          style={hasErrors() ? {} : newStyles}
          autoComplete='nope'
          onChange={doChange}
          onBlur={onLostFocus}
          {...(maxLength && { maxLength: maxLength })}
          {...(onFocus && { onFocus: onFocus })}
        />
        {dataHeyimmoContext && (
          <span data-heyimmo-context={dataHeyimmoContext} style={{ display: 'none' }} aria-hidden='true'>
            {state.currentInput ?? ''}
          </span>
        )}
        <button
          tabIndex={0}
          type='button'
          aria-label='Plus'
          aria-describedby={id + '-input-label'}
          className='stepper-increment-button'
          id='stepper-increment-button'
          onClick={stepUp}
          onBlur={onLostFocus}
          {...(onFocus && { onFocus: onFocus })}
        >
          +
        </button>
      </div>
      {hasErrors() && (
        <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
          <div className='caption caption--error'>
            <IconS24CrossCircle24Filled className={'caption-icon'} />
            {t(`validation.${errors[0]}`)}
          </div>
        </div>
      )}
    </div>
  )
}
